/**
 * Validators
 * TASK-294: Validate client context and search inputs
 *
 * Handles validation for meeting prep search
 */

import type { ValidationResult, UseMeetingPrepSearchOptions } from './types';
import type { SearchResponse } from '../../types/search.types';

/**
 * Validate client context for meeting prep
 */
export function validateClientContext(
  clientId?: string,
  clientName?: string
): ValidationResult {
  if (!clientId || clientId.trim() === '') {
    return {
      valid: false,
      error: 'CLIENT_ID_MISSING',
      action: 'redirect',
    };
  }

  if (!clientName || clientName.trim() === '') {
    return {
      valid: false,
      error: 'CLIENT_NAME_MISSING',
      action: 'fetch',
    };
  }

  return { valid: true };
}

/**
 * Validate meeting prep search options
 */
export function validateSearchOptions(options: UseMeetingPrepSearchOptions): ValidationResult {
  const clientValidation = validateClientContext(options.clientId, options.clientName);
  if (!clientValidation.valid) {
    return clientValidation;
  }

  if (!options.meetingId || options.meetingId.trim() === '') {
    return {
      valid: false,
      error: 'MEETING_ID_MISSING',
      action: 'redirect',
    };
  }

  if (!options.meetingDate || isNaN(options.meetingDate.getTime())) {
    return {
      valid: false,
      error: 'MEETING_DATE_INVALID',
      action: 'none',
    };
  }

  return { valid: true };
}

/**
 * Check if a search response is empty
 */
export function isEmptyResult(response: SearchResponse): boolean {
  return (
    response.sources.length === 0 &&
    response.toolResults.every(
      (t) => !t.data || (Array.isArray(t.data) && t.data.length === 0)
    )
  );
}

/**
 * Check if a search response has partial results
 */
export function hasPartialResults(response: SearchResponse): boolean {
  return response.status === 'partial' || response.failedSources.length > 0;
}

/**
 * Get failed sources from response
 */
export function getFailedSources(response: SearchResponse): string[] {
  return response.failedSources;
}

/**
 * Check if email OAuth error
 */
export function isEmailOAuthError(error: string): boolean {
  const oauthErrors = [
    'oauth',
    'not connected',
    'not authorized',
    'token expired',
    'authentication required',
    'access denied',
  ];
  const lowerError = error.toLowerCase();
  return oauthErrors.some((e) => lowerError.includes(e));
}

/**
 * Check if timeout error
 */
export function isTimeoutError(error: string | Error): boolean {
  const errorStr = typeof error === 'string' ? error : error.message;
  return (
    errorStr.toLowerCase().includes('timeout') ||
    errorStr.toLowerCase().includes('timed out') ||
    errorStr.includes('ETIMEDOUT')
  );
}

/**
 * Check if network error
 */
export function isNetworkError(error: string | Error): boolean {
  const errorStr = typeof error === 'string' ? error : error.message;
  return (
    errorStr.toLowerCase().includes('network') ||
    errorStr.toLowerCase().includes('failed to fetch') ||
    errorStr.includes('ECONNREFUSED') ||
    errorStr.includes('ENOTFOUND')
  );
}

/**
 * Check if auth error (401/403)
 */
export function isAuthError(error: string | Error): boolean {
  const errorStr = typeof error === 'string' ? error : error.message;
  return (
    errorStr.includes('401') ||
    errorStr.includes('403') ||
    errorStr.toLowerCase().includes('unauthorized') ||
    errorStr.toLowerCase().includes('forbidden')
  );
}

/**
 * Validate date range
 */
export function isValidDateRange(dateFrom: string, dateTo: string): boolean {
  try {
    const from = new Date(dateFrom);
    const to = new Date(dateTo);

    // Check valid dates
    if (isNaN(from.getTime()) || isNaN(to.getTime())) {
      return false;
    }

    // From should be before to
    if (from >= to) {
      return false;
    }

    // Range shouldn't be more than 2 years
    const maxRange = 2 * 365 * 24 * 60 * 60 * 1000; // 2 years in ms
    if (to.getTime() - from.getTime() > maxRange) {
      return false;
    }

    return true;
  } catch {
    return false;
  }
}
